import type { TFlowQuery } from './api';

type TFlowQueryParams = NonNullable<TFlowQuery>;
export type TFlowSort = NonNullable<TFlowQueryParams['sort']>;
export type TFlowStateFilter = NonNullable<TFlowQueryParams['state']>;

export interface IFlowFilters {
  startsFrom?: string;
  state?: TFlowStateFilter;
  hasProblems?: boolean;
  q?: string;
  sort?: TFlowSort;
  cursor?: string;
  folder?: string;
}

export const DEFAULT_FLOW_SORT: TFlowSort = 'problems';

const STATES: readonly TFlowStateFilter[] = ['active', 'paused', 'disabled'];
const SORTS: readonly TFlowSort[] = ['problems', 'traffic', 'name'];

export const parseFlowFilters = (search: Record<string, unknown>): IFlowFilters => {
  const text = (key: string) =>
    typeof search[key] === 'string' && search[key] !== '' ? (search[key] as string) : undefined;

  const state = text('state');
  const sort = text('sort');

  return {
    startsFrom: text('startsFrom'),
    state:
      state && (STATES as readonly string[]).includes(state)
        ? (state as TFlowStateFilter)
        : undefined,
    hasProblems: search.hasProblems === true || search.hasProblems === 'true' ? true : undefined,
    q: text('q'),
    sort: sort && (SORTS as readonly string[]).includes(sort) ? (sort as TFlowSort) : undefined,
    cursor: text('cursor'),
    folder: text('folder'),
  };
};

// `true`, not `'true'`: the router JSON-encodes a string that reads as JSON, and the URL said %22true%22.
export const flowFiltersToSearch = (filters: IFlowFilters): Record<string, string | boolean> => {
  const out: Record<string, string | boolean> = {};
  if (filters.startsFrom) out.startsFrom = filters.startsFrom;
  if (filters.state) out.state = filters.state;
  if (filters.hasProblems) out.hasProblems = true;
  if (filters.q) out.q = filters.q;
  if (filters.sort) out.sort = filters.sort;
  if (filters.cursor) out.cursor = filters.cursor;
  if (filters.folder) out.folder = filters.folder;
  return out;
};

export const flowFiltersToQuery = (filters: IFlowFilters): TFlowQuery => {
  const query: TFlowQuery = { sort: filters.sort ?? DEFAULT_FLOW_SORT, limit: 12 };
  if (filters.startsFrom) query.startsFrom = filters.startsFrom;
  if (filters.state) query.state = filters.state;
  if (filters.hasProblems) query.hasProblems = true;
  if (filters.q) query.q = filters.q;
  if (!flowsAcrossFolders(filters)) query.folderId = filters.folder ?? 'root';
  if (filters.cursor) query.cursor = filters.cursor;
  return query;
};

// Filtered is flat across folders: a "Schedules" link means every scheduled flow, not the top level's.
export const flowsAcrossFolders = (filters: IFlowFilters): boolean =>
  Boolean(filters.q || filters.startsFrom || filters.state || filters.hasProblems);

export const activeFlowFilterCount = (filters: IFlowFilters): number =>
  (filters.startsFrom ? 1 : 0) + (filters.state ? 1 : 0) + (filters.q ? 1 : 0);
